import { useEffect } from 'react';
import { AnimatePresence, m } from 'framer-motion';
import { getCategoryMeta, getXPForNextLevel, levelTitle } from '@brainfart/shared';
import { dismissLevelUp, useCurrentLevelUp } from '../levelUp';
import { ConfettiBurst } from './Confetti';
import { playFinish } from '../sound';
import { track } from '../telemetry';
import { checkProfileAchievements } from '../achievements';

// Mounted once at the root: a full-screen "LEVEL UP" moment whenever a game
// pushes a category over a level threshold, whatever mode it was.
export function LevelUpHost() {
  const up = useCurrentLevelUp();

  useEffect(() => {
    if (!up) return;
    playFinish();
    track({ type: 'level_up', payload: { category: up.category, level: up.level } });
    checkProfileAchievements();
  }, [up]);

  const meta = up ? getCategoryMeta(up.category) : undefined;
  const toNext = up ? getXPForNextLevel(up.xp, up.category) : null;

  return (
    <AnimatePresence>
      {up && (
        <m.div
          key={`${up.category}-${up.level}`}
          role="dialog"
          aria-modal="true"
          aria-label={`Level ${up.level} in ${meta?.title ?? up.category}`}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={dismissLevelUp}
          style={{
            position: 'fixed', inset: 0, zIndex: 1100, display: 'flex', alignItems: 'center', justifyContent: 'center',
            background: `radial-gradient(circle at 50% 38%, ${meta?.color ?? '#7C5CFF'}66 0%, transparent 60%), #0F0D1F`,
            padding: '1.5rem',
          }}
        >
          <ConfettiBurst />
          <m.div
            initial={{ scale: 0.6, y: 30 }}
            animate={{ scale: 1, y: 0 }}
            transition={{ type: 'spring', stiffness: 220, damping: 16 }}
            onClick={e => e.stopPropagation()}
            style={{ position: 'relative', zIndex: 2, textAlign: 'center', maxWidth: 360, width: '100%' }}
          >
            <m.p
              initial={{ letterSpacing: '0.1em', opacity: 0 }}
              animate={{ letterSpacing: '0.35em', opacity: 1 }}
              transition={{ delay: 0.15, duration: 0.6 }}
              style={{ color: '#FFC93C', fontWeight: 900, fontSize: '1rem', marginBottom: '1rem' }}
            >
              LEVEL UP
            </m.p>
            <m.div
              animate={{ rotate: [0, -8, 8, 0], scale: [1, 1.12, 1] }}
              transition={{ delay: 0.3, duration: 0.8 }}
              style={{ fontSize: '5rem', lineHeight: 1, marginBottom: '0.75rem' }}
            >
              {meta?.icon ?? '🧠'}
            </m.div>
            <p style={{ color: '#ddd', fontWeight: 700, fontSize: '1.1rem' }}>{meta?.title ?? up.category} · Level</p>
            <m.p
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ delay: 0.45, type: 'spring', stiffness: 260 }}
              style={{ fontSize: '4.5rem', fontWeight: 900, color: '#fff', lineHeight: 1.1 }}
            >
              {up.level}
            </m.p>
            <p style={{ fontSize: '1.4rem', fontWeight: 800, color: '#FFC93C', marginBottom: '0.5rem' }}>
              {levelTitle(up.level)}
            </p>
            <p style={{ color: '#999', fontSize: '0.9rem', marginBottom: '1.75rem' }}>
              {toNext === null ? 'Max level. Nothing left to prove.' : `${toNext.toLocaleString()} XP to ${levelTitle(up.level + 1)}`}
            </p>
            <button className="btn btn-primary" onClick={dismissLevelUp} style={{ width: '100%', padding: '1rem' }} autoFocus>
              Let's go
            </button>
          </m.div>
        </m.div>
      )}
    </AnimatePresence>
  );
}
